import { useEffect, useMemo, useCallback, useState } from 'react';
import { CardState, DeckState } from '@/components/tabletop/types';

interface DeckSearchModalProps {
  deck: DeckState;
  selectedCardIds: string[];
  onSelectionChange: (cardIds: string[]) => void;
  onClose: () => void;
  onRequestDrag: (cardIds: string[], event: React.MouseEvent<HTMLDivElement>) => void;
  zoomedCardId: string | null;
  onZoomCard: (card: CardState | null) => void;
}

function toggleCardSelection(
  current: string[],
  cardId: string,
  mode: 'replace' | 'toggle'
): string[] {
  if (mode === 'replace') {
    if (current.length === 1 && current[0] === cardId) {
      return current;
    }
    return [cardId];
  }

  const exists = current.includes(cardId);
  if (exists) {
    return current.filter(id => id !== cardId);
  }
  return [...current, cardId];
}

export default function DeckSearchModal({
  deck,
  selectedCardIds,
  onSelectionChange,
  onClose,
  onRequestDrag,
  zoomedCardId,
  onZoomCard
}: DeckSearchModalProps) {
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const orderedCards = useMemo(() => {
    return [...deck.cards].reverse();
  }, [deck.cards]);

  const totalCards = orderedCards.length;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key.toLowerCase() === 'z') {
        if (zoomedCardId) {
          event.preventDefault();
          onZoomCard(null);
          return;
        }

        const targetCardId = hoveredCardId ?? selectedCardIds[selectedCardIds.length - 1] ?? null;
        const targetCard = targetCardId ? deck.cards.find(card => card.id === targetCardId) : null;
        if (targetCard) {
          event.preventDefault();
          onZoomCard(targetCard);
        }
      }
    },
    [deck.cards, hoveredCardId, onClose, onZoomCard, selectedCardIds, zoomedCardId]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleCardMouseDown = useCallback(
    (cardId: string) => (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const useToggle = event.shiftKey || event.metaKey || event.ctrlKey;
      const alreadySelected = selectedCardIds.includes(cardId);

      let nextSelection: string[];
      if (useToggle) {
        nextSelection = toggleCardSelection(selectedCardIds, cardId, 'toggle');
      } else if (alreadySelected) {
        nextSelection = selectedCardIds;
      } else {
        nextSelection = [cardId];
      }

      if (
        nextSelection.length !== selectedCardIds.length ||
        nextSelection.some((id, index) => id !== selectedCardIds[index])
      ) {
        onSelectionChange(nextSelection);
      }

      if (nextSelection.length > 0) {
        onRequestDrag(nextSelection, event);
      }
    },
    [onSelectionChange, onRequestDrag, selectedCardIds]
  );

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onClose();
    },
    [onClose]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleOverlayClick}
      />
      <div className="relative z-10 mx-4 w-full max-w-6xl rounded-xl bg-slate-900/95 text-slate-100 shadow-2xl border border-slate-700/60">
        <div className="flex items-center justify-between border-b border-slate-700/60 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold leading-tight">{deck.name}</h2>
            <p className="text-sm text-slate-300">
              {totalCards} {totalCards === 1 ? 'card' : 'cards'} &middot; First card is next to draw
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-600 px-3 py-1 text-sm font-medium text-slate-200 hover:bg-slate-800"
          >
            Close
          </button>
        </div>

        <div className="px-6 py-3 text-sm text-slate-300 border-b border-slate-700/60">
          Click to select cards. Hover or select a card and press Z to zoom. Drag selected cards to the tabletop or your hand.
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-6">
          {orderedCards.length === 0 ? (
            <div className="rounded-lg border border-slate-700/60 bg-slate-800/80 px-6 py-16 text-center text-slate-300">
              This deck is empty.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {orderedCards.map((card, index) => {
                const isSelected = selectedCardIds.includes(card.id);
                const displayIndex = index + 1;
                const cardName = card.name || `Card ${displayIndex}`;

                return (
                  <div
                    key={card.id}
                    onMouseDown={handleCardMouseDown(card.id)}
                    onMouseEnter={() => setHoveredCardId(card.id)}
                    onMouseLeave={() => setHoveredCardId(current => (current === card.id ? null : current))}
                    className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-lg border transition ${
                      isSelected
                        ? 'border-sky-400 ring-2 ring-sky-400/60 ring-offset-2 ring-offset-slate-900'
                        : 'border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    <div className="relative w-full overflow-hidden bg-slate-800/60 pt-[140%]">
                      {card.imageUrl ? (
                        <img
                          src={card.imageUrl}
                          alt={cardName}
                          className="absolute inset-0 h-full w-full object-cover"
                          draggable={false}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
                          No image
                        </div>
                      )}
                      <span className="absolute left-2 top-2 rounded bg-slate-900/80 px-2 py-0.5 text-xs font-semibold text-slate-100">
                        #{displayIndex}
                      </span>
                    </div>
                    <div className="border-t border-slate-700 bg-slate-900/60 px-3 py-2 text-sm font-medium leading-tight text-slate-100">
                      {cardName}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
